import { useIntl } from "react-intl";

import { CircleCheckSolid } from "~/hec/assets/icons";
import { BodyCopy } from "~/hec/components/ui/body-copy";
import { Heading } from "~/hec/components/ui/heading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "~/hec/components/ui/table";
import { cn } from "~/lib/ui";

import messages from "./messages";
import { goalHasBenefit, isRecommendedGoal } from "./questionnaire-logic";
import type {
  BenefitEntry,
  DisclaimerItem,
  GoalData,
  GoalEntry,
} from "./types";

/**
 * GoalBenefitsTable — the goals × benefits comparison grid below the quiz
 * (SFCC-11290). Ported from composable `goalBenefitsTable.jsx`, including its
 * styling (SFCC-11738): the `Table` UI family + flex rows (`classes` below) so
 * the grid matches production — the earlier raw-`<table>` port rendered with the
 * wrong layout/heights/tokens. Each row is a benefit; each goal column shows a
 * check when that goal carries the benefit; the recommended goal's matching
 * cells highlight (`bg-active` / `text-active-foreground`).
 *
 * Note vs composable: the mobile expand/collapse (`ExpandContent`) is omitted —
 * Workers has no such primitive yet — so the full table renders on mobile.
 */
interface GoalBenefitsTableProps {
  currentGoal: GoalData | null;
  bgImage?: string;
  isMobile: boolean;
  benefits: BenefitEntry[];
  goals: GoalEntry[];
  disclaimerText?: DisclaimerItem[];
}

const GoalBenefitsTable = ({
  currentGoal,
  bgImage,
  isMobile,
  benefits,
  goals,
  disclaimerText,
}: GoalBenefitsTableProps) => {
  const intl = useIntl();

  const classes = {
    tableRow:
      "flex justify-start h-10 sm:h-16 lg:h-20 lg:justify-center border-none my-0.5 lg:my-2",
    tableHead: "rounded-lg flex items-center text-primary h-full sm:p-3 p-1",
    tableCell:
      "rounded-lg flex items-center sm:p-3 p-1 justify-center w-2/12 text-center mx-0.5 lg:mx-1",
    text: cn(
      "font-medium mx-auto text-[.4rem] lg:text-[.65rem] leading-tight",
      {
        "text-[.3rem] leading-tight": isMobile,
      },
    ),
  };

  const heading = (
    <Heading
      tag="h4"
      variant="h4"
      className="my-4 text-center text-black"
      data-testid="goalBenefitsTable-heading"
    >
      {intl.formatMessage(messages.goalsAndBenefitsHeading)}
    </Heading>
  );

  const table = (
    <Table
      className="table-fixed"
      id="benefitsTable"
      data-testid="goalBenefitsTable"
    >
      <TableBody
        className="border-none"
        data-testid="goalBenefitsTable-TableBody"
      >
        {benefits.map((benefitEntry, benefitIdx) => {
          const benefitValue = benefitEntry.data.benefit;
          return (
            <TableRow
              key={`benefit-row-${benefitIdx}`}
              className={classes.tableRow}
              data-testid="goalBenefitsTable-row"
            >
              <TableHead
                className={cn(
                  classes.tableHead,
                  "theme-medium mr-0.5 h-full w-1/4 bg-background lg:mr-1",
                )}
                data-testid="goalBenefitsTable-TableHead"
              >
                <p className={cn(classes.text, "text-primary")}>
                  {benefitValue}
                </p>
              </TableHead>
              {goals.map((goalEntry, goalIdx) => {
                const hasBenefit = goalHasBenefit(goalEntry.data, benefitValue);
                // Highlight the recommended goal's matching cells. Match on the
                // stable Builder entry hash (SFCC-11740) — the author-set
                // `data.id` slug can be absent/non-unique, which left the
                // highlight stuck on the wrong column.
                const isActive =
                  hasBenefit && isRecommendedGoal(currentGoal, goalEntry);
                return (
                  <TableCell
                    key={`benefit-${benefitIdx}-goal-${goalIdx}`}
                    className={cn(classes.tableCell, "bg-gray-05/30", {
                      "bg-active": isActive,
                    })}
                    data-testid="goalBenefitsTable-TableCell"
                  >
                    {hasBenefit && (
                      <CircleCheckSolid
                        className={cn("h-3/4 w-full text-gray-02", {
                          "text-active-foreground": isActive,
                        })}
                      />
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          );
        })}

        {/* Bottom goal-title header row */}
        <TableRow
          className={classes.tableRow}
          data-testid="goalBenefitsTable-TableRow"
        >
          <TableHead
            className={cn(classes.tableHead, "invisible w-1/4 lg:mr-1")}
            aria-hidden="true"
            data-testid="goalBenefitsTable-TableHead"
          />
          {goals.map((goalEntry, i) => (
            <TableCell
              key={`goal-title-${i}`}
              className={cn(classes.tableCell, "bg-accent")}
              data-testid="goalBenefitsTable-TableCell"
            >
              <p className={cn(classes.text, "text-white")}>
                {goalEntry.data.title}
              </p>
            </TableCell>
          ))}
        </TableRow>
      </TableBody>
    </Table>
  );

  return (
    <div
      className={cn("bg-cover bg-center bg-no-repeat p-4 lg:p-10", {
        "bg-gray-01": isMobile,
      })}
      style={!isMobile && bgImage ? { backgroundImage: `url(${bgImage})` } : {}}
    >
      {isMobile && heading}
      <div className="flex flex-col items-center bg-white px-2 py-4 lg:mx-20 lg:p-20">
        {!isMobile && heading}
        {table}
        {disclaimerText && disclaimerText.length > 0 && (
          <div className="ml-[15%] mt-2 w-3/4">
            {disclaimerText.map(({ text }, idx) => (
              <BodyCopy
                key={`disclaimer-${idx}`}
                size="small"
                className={cn(
                  "mx-auto !font-medium",
                  isMobile ? "!leading-3" : "!leading-6",
                )}
                style={{ fontSize: isMobile ? ".3rem" : ".65rem" }}
                data-testid={`goalBenefitsTable-disclaimer-${idx}`}
              >
                {text}
              </BodyCopy>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GoalBenefitsTable;
